import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Sparkles, Check, X, SkipForward, AudioLines, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * v260.20 — Verify AI Imports review mode.
 *
 * Walks the user through every AI-imported field one at a time
 * (think GitHub PR review). For each entry:
 *   - shows the section/field label + the verbatim source quote
 *   - shows the current field value
 *   - inline `<audio>` autoplays at the matching segment timestamp
 *   - Approve = clear the provenance chip (field is now human-verified)
 *   - Reject  = clear the field value AND the provenance chip
 *   - Skip    = leave both as-is, advance to next
 *
 * State lives in the parent doc — this dialog only emits intents back
 * via `onVerify(secId, fieldId, action)`.
 */
export default function VerifyImportsDialog({
  open, onOpenChange, doc, catalog, estimateId, onVerify,
}) {
  // Build a flat queue from the field_provenance map. Sort by section
  // order in the taxonomy so the review feels natural top-to-bottom.
  const queue = useMemo(() => {
    const fp = doc?.field_provenance || {};
    const sectionOrder = (catalog?.narrative_taxonomy || []).map((s) => s.id);
    const sectionsById = {};
    for (const sec of catalog?.narrative_taxonomy || []) sectionsById[sec.id] = sec;
    const items = [];
    for (const secId of sectionOrder) {
      const fields = fp[secId] || {};
      const fieldOrder = (sectionsById[secId]?.fields || []).map((f) => f.id);
      const fieldIds = [...new Set([
        ...fieldOrder.filter((fid) => fid in fields),
        ...Object.keys(fields),
      ])];
      for (const fid of fieldIds) {
        const meta = sectionsById[secId]?.fields?.find((f) => f.id === fid);
        items.push({
          sectionId: secId,
          sectionLabel: sectionsById[secId]?.label || secId,
          fieldId: fid,
          fieldLabel: meta?.label || fid,
          provenance: fields[fid],
          currentValue: (doc?.narrative?.[secId] || {})[fid],
        });
      }
    }
    return items;
  }, [doc, catalog]);

  const [cursor, setCursor] = useState(0);
  const audioRef = useRef(null);

  // Reset cursor whenever the dialog opens fresh.
  useEffect(() => {
    if (open) setCursor(0);
  }, [open]);

  const current = queue[cursor];
  const totalRemaining = queue.length;
  const isDone = !current;

  // Auto-load + auto-play the audio at the segment timestamp when
  // the cursor moves. Falls back gracefully if the browser blocks
  // autoplay or the recording has been deleted.
  useEffect(() => {
    if (!open || !current || !audioRef.current) return;
    const a = audioRef.current;
    const onLoaded = () => {
      try {
        if (current.provenance?.start_seconds != null) {
          a.currentTime = current.provenance.start_seconds;
        }
        a.play().catch(() => { /* autoplay blocked — user can press play */ });
      } catch { /* noop */ }
      a.removeEventListener('loadedmetadata', onLoaded);
    };
    a.addEventListener('loadedmetadata', onLoaded);
    // If metadata is already loaded (cached), trigger immediately.
    if (a.readyState >= 1) onLoaded();
    return () => a.removeEventListener('loadedmetadata', onLoaded);
  }, [open, current]);

  const handleAction = (action) => {
    if (!current) return;
    onVerify(current.sectionId, current.fieldId, action);
    // Advance cursor — but since the parent mutation may shrink the
    // queue (removing the just-handled entry), staying at the same
    // cursor index naturally surfaces the next item.
    if (action === 'skip') setCursor((c) => c + 1);
    // For approve/reject the parent removes the provenance entry so
    // the queue shrinks — leave cursor in place.
  };

  const handleClose = () => onOpenChange(false);

  const audioSrc = current?.provenance?.attachment_id
    ? `${API}/api/estimates/${estimateId}/requirements/attachments/${current.provenance.attachment_id}`
    : null;

  const fmtValue = (v) => {
    if (v === undefined || v === null || v === '') return <em className="text-slate-400">(empty)</em>;
    if (Array.isArray(v)) {
      if (!v.length) return <em className="text-slate-400">(empty list)</em>;
      if (v.every((x) => typeof x === 'object')) {
        return v.map((r, i) => (
          <div key={i} className="text-xs">
            {Object.entries(r).map(([k, val]) => (
              <span key={k} className="mr-2"><b>{k}:</b> {String(val || '—')}</span>
            ))}
          </div>
        ));
      }
      return v.join(', ');
    }
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const stampStr = (() => {
    const s = current?.provenance?.start_seconds;
    if (s == null) return null;
    const sec = Math.max(0, Math.floor(s));
    return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`;
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="verify-imports-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            Verify AI Imports
            {totalRemaining > 0 && (
              <Badge variant="outline" className="ml-2 border-cyan-300 bg-cyan-50 text-cyan-700">
                {totalRemaining} pending
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Review each AI-imported field against its source recording.
            Approve to clear the chip (field is now human-verified) or
            reject to discard the value.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2 space-y-4">
          {isDone ? (
            <div className="py-12 text-center" data-testid="verify-imports-done">
              <Check className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <div className="text-base font-semibold text-slate-800">All AI imports reviewed</div>
              <div className="text-xs text-slate-500 mt-1">
                Your Requirements Document is fully human-verified.
              </div>
            </div>
          ) : (
            <>
              {/* Breadcrumb */}
              <div className="flex items-center gap-1 text-xs text-slate-500" data-testid="verify-breadcrumb">
                <span>{current.sectionLabel}</span>
                <ChevronRight className="w-3 h-3" />
                <span className="font-medium text-slate-700">{current.fieldLabel}</span>
              </div>

              {/* Source quote */}
              {current.provenance?.quote && (
                <div className="bg-cyan-50 border-l-4 border-cyan-400 rounded-r-md px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-semibold mb-1 flex items-center gap-1">
                    <AudioLines className="w-3 h-3" />
                    Source quote {stampStr && <span>· {stampStr}</span>}
                  </div>
                  <div
                    className="text-sm italic text-slate-800 leading-relaxed"
                    data-testid="verify-source-quote"
                  >
                    &ldquo;{current.provenance.quote}&rdquo;
                  </div>
                </div>
              )}

              {/* Audio player */}
              {audioSrc ? (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                  <audio
                    ref={audioRef}
                    src={audioSrc}
                    controls
                    preload="metadata"
                    className="w-full h-8"
                    data-testid="verify-audio-player"
                  >
                    Your browser does not support inline audio playback.
                  </audio>
                </div>
              ) : (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Source recording no longer available — this field was AI-imported but the audio attachment has been removed.
                </div>
              )}

              {/* Current value */}
              <div className="bg-white border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Current field value
                </div>
                <div className="text-sm text-slate-800 break-words" data-testid="verify-current-value">
                  {fmtValue(current.currentValue)}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-3 gap-2">
          {isDone ? (
            <Button onClick={handleClose} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="verify-close-btn">
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleAction('skip')}
                className="border-slate-300 text-slate-600"
                data-testid="verify-skip-btn"
              >
                <SkipForward className="w-4 h-4 mr-1.5" /> Skip
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAction('reject')}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="verify-reject-btn"
              >
                <X className="w-4 h-4 mr-1.5" /> Reject &amp; clear
              </Button>
              <Button
                onClick={() => handleAction('approve')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="verify-approve-btn"
              >
                <Check className="w-4 h-4 mr-1.5" /> Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
